/*
  Component: Mega Menu
  Responsibility: open and close the header's drop-down panels, and keep the aria-expanded
                  of every control that points at one honest.

  It does nothing else. Which panel exists, what is in it and how it is laid out are
  answered by snippets/kl-mega-menu.liquid and assets/kl-mega-menu.css; this file only ever
  moves two classes on a list item.

  The markup renders closed and the stylesheet draws it closed, so there is no state for
  this file to establish — it only wires the controls up. That is a change from how it
  worked before: it used to add an .is-mega-menu-enhanced marker that the stylesheet waited
  for, so the browser painted every panel expanded in the flow until this deferred script
  ran. The fallback for a browser that will not run it is assets/kl-mega-menu-no-js.css,
  loaded from a <noscript> in layout/theme.liquid, which opens the panels in the flow and
  hides the drop-down button.

  Two ways in, because a pointer and a finger are not the same input:

    hover   guarded by a (hover: hover) media query — a capability question, not a width
            one, so there is still no breakpoint in here. On a touch screen mouseenter
            fires on tap in most browsers, and without the guard the first tap on a link
            would both open the panel and follow the link.

    click   the drop-down button, which assets/kl-mega-menu.css only puts on screen where
            hovering cannot work. It is the touch control and the only one there.

  Keyboard needs neither: focus moving into an item opens it, because the panel's links are
  the next thing in the tab order, and Escape closes it and hands focus back.

  The close delay is not a wait for the DOM. Visually the panel is detached from its link —
  it sits under the whole bar — so a pointer travelling from one to the other crosses the
  bar's padding, which belongs to neither. Those few milliseconds are that crossing. It
  also stops the panel flickering when a pointer sweeps across the row.
*/

const CLOSE_DELAY_MS = 160;

function setMegaMenuState(itemElement, isOpen) {
  itemElement.classList.toggle('is-mega-menu-open', isOpen);

  const headerElement = itemElement.closest('.kl-header');
  if (headerElement) {
    const hasOpenMenu = headerElement.querySelector('.is-mega-menu-open') !== null;
    headerElement.classList.toggle('is-mega-menu-open', hasOpenMenu);
  }

  /*
    Both the link and the button carry aria-expanded, and both are updated, because which
    of the two a visitor is offered is decided in CSS by whether hovering works. Exactly
    one is ever displayed, so exactly one is ever in the accessibility tree — but the
    hidden one must not be left claiming the opposite of the truth.
  */
  itemElement.querySelectorAll('[data-kl-mega-menu-control]').forEach((control) => {
    control.setAttribute('aria-expanded', isOpen);
  });
}

function initMegaMenuNav(navElement) {
  const itemElements = navElement.querySelectorAll('[data-kl-mega-menu-item]');

  if (itemElements.length === 0) return;

  const hoverQuery = matchMedia('(hover: hover)');

  /*
    Held in a variable rather than read back off the DOM, so nothing in here has to select
    by class — the state classes belong to the stylesheet. It doubles as the "only one
    panel at a time" rule: opening one is closing whatever this points at.
  */
  let openItemElement = null;
  let closeTimerId = null;

  function cancelScheduledClose() {
    if (closeTimerId === null) return;

    clearTimeout(closeTimerId);
    closeTimerId = null;
  }

  function closeOpenItem() {
    cancelScheduledClose();

    if (!openItemElement) return;

    setMegaMenuState(openItemElement, false);
    openItemElement = null;
  }

  function openItem(itemElement) {
    cancelScheduledClose();

    if (openItemElement === itemElement) return;

    if (openItemElement) setMegaMenuState(openItemElement, false);

    openItemElement = itemElement;
    setMegaMenuState(itemElement, true);
  }

  function scheduleClose() {
    cancelScheduledClose();
    closeTimerId = setTimeout(closeOpenItem, CLOSE_DELAY_MS);
  }

  itemElements.forEach((itemElement) => {
    const panelElement = itemElement.querySelector('[data-kl-mega-menu-panel]');
    const toggleElement = itemElement.querySelector('[data-kl-mega-menu-toggle]');

    if (!panelElement) return;

    itemElement.addEventListener('mouseenter', () => {
      if (!hoverQuery.matches) return;

      openItem(itemElement);
    });

    itemElement.addEventListener('mouseleave', () => {
      if (!hoverQuery.matches) return;

      scheduleClose();
    });

    /*
      relatedTarget is where focus came from. Ignoring moves that started inside the item is
      what lets Escape hand focus back to the trigger without the panel springing open again.

      The button is excluded outright: a click focuses it before it fires, so opening here
      would race its own handler and a tap would open then immediately close.
    */
    itemElement.addEventListener('focusin', (event) => {
      if (event.target === toggleElement) return;
      if (itemElement.contains(event.relatedTarget)) return;

      openItem(itemElement);
    });

    if (!toggleElement) return;

    toggleElement.addEventListener('click', () => {
      if (openItemElement === itemElement) {
        closeOpenItem();
        return;
      }

      openItem(itemElement);
    });
  });

  /*
    Escape is the expected way out of an overlay and focus may be anywhere inside the panel
    when it is pressed, hence the document rather than the item. Focus goes back to the
    control that opened it so the tab order does not restart at the top of the row.
  */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!openItemElement) return;

    const controlElement = openItemElement.querySelector('[data-kl-mega-menu-control]');

    closeOpenItem();

    if (controlElement) controlElement.focus();
  });

  /*
    A click outside the open item closes it. Same reasoning as Escape: the panel covers the
    page, so the first instinct is to click past it rather than to find the control again.
    No focus move — the click has already said where the visitor is going.
  */
  document.addEventListener('click', (event) => {
    if (!openItemElement) return;
    if (openItemElement.contains(event.target)) return;

    closeOpenItem();
  });

  /*
    Theme editor only. Selecting a Mega menu block in the sidebar must show the panel it
    belongs to, or a merchant is configuring something they cannot see. The event fires on
    the element carrying block.shopify_attributes, which is the panel itself.
  */
  document.addEventListener('shopify:block:select', (event) => {
    if (!navElement.contains(event.target)) return;

    const itemElement = event.target.closest('[data-kl-mega-menu-item]');

    if (!itemElement) return;

    openItem(itemElement);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    if (!navElement.contains(event.target)) return;

    closeOpenItem();
  });
}

document.querySelectorAll('[data-kl-mega-menu-nav]').forEach(initMegaMenuNav);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-mega-menu-nav]').forEach(initMegaMenuNav);
});
